import { StaticSiteDataContext } from "../../contexts/StaticSiteDataContext";
import WeddingRSVPForm from "../../guest-management/forms/RSVPForm/Wedding/WeddingRSVPForm";
import RSVPSearchModal from "../../guest-management/forms/RSVPSearchModal/RSVPSearchModal";
import { Configuration, RSVPData, Site } from "../../resources/modelTypes";
import { RootState } from "../../resources/reduxTypes";
import { selectSite } from "../../website/selectors";
import { css } from "@emotion/react";
import { Button as FancyButton } from "@minted/fancyclothes";
import { styleUtils, TextInput, theme } from "@minted/minted-components";
import React, { memo, useCallback, useContext, useState } from "react";
import { connect } from "react-redux";

const rsvpStyles = {
  cookieError: css`
    ${theme.typeStyles.bodyMediumStyles};
    text-align: center;
    color: ${theme.colors.negative800};
    margin-bottom: ${theme.spacing.x2};
  `,
  nameInput: css`
    margin-bottom: ${theme.spacing.x4};
    text-align: left;
  `,
  wrapper: css`
    max-width: ${styleUtils.rem(500)};
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${theme.spacing.x4};
  `,
};

export type DisplayRSVPProps = {
  configuration: Configuration;
  isPreview: boolean;
};

type DisplayRSVPPropsWithRedux = DisplayRSVPProps & {
  site: Site;
};

const DisplayRSVP = ({
  configuration,
  isPreview,
  site,
}: DisplayRSVPPropsWithRedux) => {
  const { isDemoSite } = useContext(StaticSiteDataContext);
  const [rsvpData, setRsvpData] = useState<RSVPData | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const isInBrowser = typeof window !== "undefined";
  const hasCookies = isInBrowser && window.navigator.cookieEnabled;

  const openModal = useCallback(() => {
    setShowRSVPModal(true);
  }, []);
  const closeModal = useCallback(() => {
    setShowRSVPModal(false);
  }, []);

  const updateSearchTerm = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);
  const onKeyDown = useCallback(
    (event) => event.key === "Enter" && setShowRSVPModal(true),
    [],
  );

  return rsvpData ? (
    <WeddingRSVPForm
      contact={rsvpData.contact}
      events={rsvpData.events}
      guests={rsvpData.guests}
    />
  ) : (
    <div css={rsvpStyles.wrapper}>
      {configuration.rsvpSubheading || "Enter Your Name to RSVP"}
      {hasCookies ? (
        <>
          {showRSVPModal && (
            <RSVPSearchModal
              closeModal={closeModal}
              configuration={configuration}
              initialSearchTerm={searchTerm}
              openInvitation={false}
              setRsvpData={setRsvpData}
              site={site}
            />
          )}
          <div css={rsvpStyles.nameInput}>
            <TextInput
              disabled={isPreview || isDemoSite}
              onChange={updateSearchTerm}
              onKeyDown={onKeyDown}
              size="small"
              touched
              value={searchTerm}
            />
          </div>
          <FancyButton
            block={true}
            disabled={isPreview || isDemoSite}
            onClick={openModal}
            text="Find RSVP"
          />
        </>
      ) : (
        <div css={rsvpStyles.cookieError}>
          Cookies are required to submit your RSVP. Please enable cookies in
          your browser.
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  site: selectSite(state.resources.sites),
});

const ConnectedDisplayRSVP = connect(mapStateToProps)(
  // @ts-expect-error Force types for redux/react connection
  DisplayRSVP,
) as unknown as React.FC<DisplayRSVPProps>;

const MemoizedDisplayRSVP = memo(ConnectedDisplayRSVP);

export { MemoizedDisplayRSVP as DisplayRSVP };
